import { TElement } from '@udecode/plate';
import { formatISO } from 'date-fns';
import { BaseRange } from 'slate';
import { v4 as uuid } from 'uuid';
import { create } from 'zustand';

export interface Comment {
  content: TElement[];
  id: string;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: string;
    email: string;
    firstname: string;
    lastname?: string;
    avatar: string;
  };
  replies?: Comment[];
}

export interface CommentsState {
  activeComment: {
    id?: string;
    range?: Range;
    selection?: BaseRange;
  } | null;

  comments: {
    [key: string]: Comment;
  };

  addComment: (d: {
    id?: string;
    value: TElement[];
    user: Comment['owner'];
  }) => Promise<null | string>;
  deleteComment: (d: { id: string }) => Promise<boolean>;
  setActiveComment: (d: CommentsState['activeComment'] | null) => void;

  callbacks?: {
    createCommentThread?: (d: {
      value: TElement[];
      user: Comment['owner'];
    }) => Promise<null | string>;
    addCommentResponse?: (d: {
      commentId: string;
      value: TElement[];
      user: Comment['owner'];
    }) => Promise<void | string>;
    fetchComment?: (d: { id: string }) => Promise<Comment | null>;
    deleteCommentThread?: (d: { id: string }) => Promise<boolean>;
  };

  setCallbacks: (d: CommentsState['callbacks']) => void;
}

const zustandImmer = (config) => config;

export const useComments = create<CommentsState>()((set, get) => ({
  activeComment: null,
  comments: {},
  setActiveComment: async (d) => {
    // console.log('d', d);
    set(() => ({
      activeComment: d,
    }));

    if (d?.id && get().callbacks?.fetchComment) {
      const comment = await get().callbacks?.fetchComment?.({ id: d.id });

      if (comment) {
        set((s) => {
          if (d.id) {
            s.comments[d.id] = comment;
          }
          return s;
        });
      }
    }
  },
  addComment: async ({ id, value, user }) => {
    let newId = uuid();
    let responseId = uuid();
    const newDate = formatISO(new Date());
    if (get().callbacks?.createCommentThread && !id) {
      const _id = await get().callbacks?.createCommentThread?.({ user, value: value });
      if (_id) {
        newId = _id;
      } else {
        return null;
      }
    } else if (id) {
      const _id = await get().callbacks?.addCommentResponse?.({
        commentId: id,
        user: user,
        value,
      });
      if (_id) {
        responseId = _id;
      }
    }
    set((s) => {
      if (id && s.comments?.[id]) {
        if (!s.comments[id].replies) {
          s.comments[id].replies = [];
        }
        s.comments[id]?.replies?.push({
          id: responseId,
          content: value,
          owner: user,
          createdAt: newDate,
          updatedAt: newDate,
        });
      } else {
        s.comments[newId] = {
          content: value,
          replies: [],
          owner: user,
          createdAt: newDate,
          id: newId,
          updatedAt: newDate,
        };
      }
      return s;
    });
    if (!id) {
      return newId;
    }
    return null;
  },
  deleteComment: async ({ id }) => {
    if (get().callbacks?.deleteCommentThread && id) {
      const isDeleted = await get().callbacks?.deleteCommentThread?.({ id });
      if (isDeleted) {
        set((s) => {
          if (s.comments[id]) {
            delete s.comments[id];
          }
          return s;
        });
        return true;
      }
      return false;
    }
    set((s) => {
      if (s.comments[id]) {
        delete s.comments[id];
      }
      return s;
    });
    return true;
  },
  setCallbacks: (callbacks) =>
    set((s) => {
      s.callbacks = { ...s.callbacks, ...callbacks };
      return s;
    }),
}));
